import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import { type ReactNode, useRef, useState } from 'react'
import {
	StoreDetails,
	StoreTitle,
} from '#app/components/showrooms/store-info.tsx'
import { type IStoreWithImage } from '#app/components/showrooms/types.ts'
import { Icon } from '#app/components/ui/icon.tsx'
import { StoreType } from '#app/enums/store-type.ts'
import { useIsMobile } from '#app/hooks/use-mobile.tsx'
import { useLocale } from '#app/utils/i18n.tsx'
import { cn, getStoreImageSrc } from '#app/utils/misc.tsx'

export default function StoreHoverItem({
	store,
	...props
}: {
	store: IStoreWithImage
	children: ReactNode
} & Omit<HTMLMotionProps<'div'>, 'ref' | 'children'>) {
	const locale = useLocale()
	const isMobile = useIsMobile()
	const [isOpen, setIsOpen] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)

	const handleDotClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (isMobile) {
			// On mobile, go to link directly
			if (store.link) {
				const a = window.document.createElement('a')
				a.href = store.link
				a.target = '_blank'
				document.body.appendChild(a).click()
			}
		} else {
			// On desktop, toggle the info panel
			setIsOpen(!isOpen)
		}
	}

	return (
		<>
			<motion.div
				onClick={handleDotClick}
				{...props}
				className={cn('relative cursor-pointer', props.className)}
			>
				{props.children}
			</motion.div>

			{/* Desktop: Bottom sheet */}
			{!isMobile && (
				<AnimatePresence>
					{isOpen && (
						<>
							{/* Backdrop */}
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 0.5 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								className="fixed inset-0 z-40 bg-black"
								onClick={() => setIsOpen(false)}
							/>

							{/* Bottom sheet */}
							<motion.div
								ref={contentRef}
								initial={{ y: '100%' }}
								animate={{ y: 0 }}
								exit={{ y: '100%' }}
								transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
								className="fixed bottom-0 left-navigation right-0 z-50 flex max-h-[70vh] flex-col gap-md overflow-auto bg-white p-lg shadow-2xl md:flex-row md:p-xl"
							>
								<button
									onClick={() => setIsOpen(false)}
									className="absolute right-4 top-4 rounded-full p-1 hover:bg-gray-100"
									aria-label="Close"
								>
									<Icon name="cross-1" size="md" />
								</button>

								<img
									src={getStoreImageSrc(store.storeImageId)}
									className={
										'h-[30vh] max-h-[30vh] w-full flex-shrink-0 object-cover md:h-auto md:w-[40%] lg:w-[35%]'
									}
									alt={StoreType.getByCode(store.type)?.labels[locale]}
								/>
								<div className={'flex flex-1 flex-col gap-sm pt-sm md:pt-md'}>
									<StoreTitle store={store} />
									<StoreDetails store={store} />

									{store.link && (
										<a
											href={store.link}
											target="_blank"
											rel="noopener noreferrer"
											className="mt-sm text-sm underline hover:no-underline"
										>
											Visit website
										</a>
									)}
								</div>
							</motion.div>
						</>
					)}
				</AnimatePresence>
			)}
		</>
	)
}
